/**
 * 教学内容数据（入门到进阶）—— 情景化 + 分情况演示版
 *
 * 每个 lesson：
 *   - body：正文 HTML
 *   - diagram：可选，'structure' 在正文上方插入结构图解
 *   - goal：可选，目标态卡片 { caption, highlight }（目标态取 demo 第一个案例真实计算）
 *   - demo：3D 情景演示 {
 *       view: 'top' | 'bottom' | 'front'   // 初始视角（bottom = 仰视看白色底面）
 *       highlight: 'D_EDGES' 等             // 展开图高亮标识
 *       cases: [{
 *         id, label,        // 案例切换按钮文字
 *         brief,            // 案例一句话说明（显示在 3D 上方）
 *         setup, formula,   // 真实 move 序列：setup 构造起始态，formula 是演示内容
 *         notes: [...]      // 与 formula 每一步一一对应的中文解说
 *       }]
 *     }
 *
 * 状态合法性：所有 setup/formula 都经 cubejs 逐一验证（setup+formula 可达目标态）。
 * ⚠️ 修改任何序列后请运行仓库根目录 verify-lessons 脚本复核。
 */

import { CFOP_SECTION } from './lessons-cfop.js';

/** 生成"掏让放回"四步循环的逐步解说；milestones[r] 是第 r+1 遍结束时的进度提示 */
function sexyNotes(reps, milestones = []) {
  const base = [
    'R —— ①掏：右层向上提（把角块和它的槽位一起提起来）',
    'U —— ②让：顶层转一下，给角块换个姿势',
    "R' —— ③放：右层放回原位",
    "U' —— ④回：顶层转回来",
  ];
  const out = [];
  for (let r = 0; r < reps; r++) {
    for (let i = 0; i < 4; i++) {
      let n = reps > 1 ? `第 ${r + 1}/${reps} 遍 · ${base[i]}` : base[i];
      if (i === 3 && milestones[r]) n = `${reps > 1 ? `第 ${r + 1}/${reps} 遍 · ` : ''}U' —— ④回：顶层转回来。${milestones[r]}`;
      out.push(n);
    }
  }
  return out;
}

const rep = (s, n) => Array(n).fill(s).join(' ');

export const SECTIONS = [
  {
    id: 'basics',
    title: '基础',
    subtitle: '认识魔方与记法',
    icon: '🧊',
    lessons: [
      {
        id: 'structure',
        title: '魔方的结构',
        diagram: 'structure',
        body: `
          <p>3×3×3 魔方由 <b>26 个可见小块</b>组成，分为三类。点击上方按钮切换，看看每一类都在魔方的什么位置：</p>
          <ul>
            <li><b>中心块 ×6</b>：每个面正中间 1 个，<b>颜色固定、永远不动</b>。它决定了该面的颜色——白对黄、红对橙、蓝对绿，这是判断朝向的根本依据。</li>
            <li><b>棱块 ×12</b>：每条棱中间的小块，有 <b>2 个颜色</b>。</li>
            <li><b>角块 ×8</b>：每个角上的小块，有 <b>3 个颜色</b>。</li>
          </ul>
          <p class="muted">💡 关键认知：因为中心块不动，所以"还原"的本质就是——把所有棱块和角块转回它们该在的位置，让每面的颜色和中心块一致。换句话说：<b>每个小块都有自己唯一的"家"，家的地址就写在它自己的颜色上</b>。这个认知贯穿整个教程。</p>
        `,
      },
      {
        id: 'notation',
        title: '官方记法（Notation）',
        body: `
          <p>魔方的每一步转动用字母表示，称为 <b>Singmaster 记法</b>。六个面各用一个字母：</p>
          <table class="lesson-table">
            <tr><th>字母</th><th>面</th><th>本教程颜色</th></tr>
            <tr><td><b>U</b></td><td>Up 上面</td><td>黄</td></tr>
            <tr><td><b>D</b></td><td>Down 下面</td><td>白</td></tr>
            <tr><td><b>F</b></td><td>Front 前面</td><td>绿</td></tr>
            <tr><td><b>B</b></td><td>Back 后面</td><td>蓝</td></tr>
            <tr><td><b>R</b></td><td>Right 右面</td><td>红</td></tr>
            <tr><td><b>L</b></td><td>Left 左面</td><td>橙</td></tr>
          </table>
          <p class="muted">📌 注意：层先法的传统持法是<b>白色朝下、黄色朝上</b>，本教程和 3D 演示全程保持这个姿势。字母指的是"位置"不是"颜色"——U 永远指最上面那层。</p>
          <p>每个字母可加后缀改变转动方向：</p>
          <ul>
            <li><b>无后缀</b>（如 <code>R</code>）：该面顺时针转 90°（<b>想象你正对着那个面看它</b>，顺时针）</li>
            <li><b>'</b> 撇号（如 <code>R'</code>）：逆时针转 90°</li>
            <li><b>2</b>（如 <code>R2</code>）：转 180°（顺逆都行，结果一样）</li>
          </ul>
          <p class="muted">💡 下面用 3D 演示走一遍 <code>R U R' U'</code>——这套"掏、让、放、回"四步小连招是整个层先法出现频率最高的动作，先在这里跟着单步走一遍，练熟记法。</p>
        `,
        demo: {
          view: 'front',
          highlight: [],
          cases: [
            {
              id: 'sexy',
              label: "四步连招 R U R' U'",
              brief: '出现频率最高的四步动作，口诀：掏、让、放、回',
              setup: "U R U' R'",
              formula: "R U R' U'",
              notes: [
                'R —— 右面顺时针转 90°（正对右面看是顺时针，从你的视角看是"右层向上提"）',
                'U —— 顶层顺时针转 90°（从上往下看顺时针，从你的视角看是"顶层向左转"）',
                "R' —— 右面逆时针 90°：把刚才提起的右层放回去",
                "U' —— 顶层逆时针 90°：顶层转回来。四步走完，注意看右下角的白色角块回家了",
              ],
            },
          ],
        },
        goal: {
          caption:
            '演示里这四步恰好把一个白色角块送回了右下角。后面第 2 步还原白色角块时，这套连招会反复用到——现在只需要练熟"掏、让、放、回"的手感',
        },
      },
    ],
  },
  {
    id: 'beginner',
    title: '层先法 · 七步还原',
    subtitle: '新手最常用的还原方法，一层一层地做',
    icon: '🎯',
    lessons: [
      {
        id: 'overview',
        title: '七步还原总览',
        body: `
          <p><b>层先法</b>的思路是：一层一层地还原，从下往上。先把白色面（底面）做好，再做中层，最后处理黄色面（顶层）。</p>
          <p>为什么从下往上？因为底面做好后，转动顶层（U）不会破坏已完成的底面——这是整个方法能成立的关键。</p>
          <ol class="lesson-steps">
            <li><b>白色十字</b>：底面四个白色棱块归位，形成十字</li>
            <li><b>白色角块</b>：四个白色角块归位，底面完成</li>
            <li><b>中层棱块</b>：四个中层棱块归位，前两层完成</li>
            <li><b>黄色十字</b>：顶面四个黄色棱块朝上，形成十字</li>
            <li><b>黄色面全黄</b>：顶面全部变黄（角块也朝上）</li>
            <li><b>顶层角块位置</b>：四个角块归位</li>
            <li><b>顶层棱块位置</b>：四个棱块归位 → 完成！</li>
          </ol>
          <p>全程只需要记住 <b>5 个公式</b>，其中第 1、2 步几乎不用公式（靠一套四步连招）。每一节都配有<b>分情况的 3D 演示</b>：你在真魔方上遇到哪种情况，就点哪个案例看演示，支持单步播放和倒退。</p>
          <p class="muted">💡 学习建议：拿一个真魔方跟着做。每一步先看"目标长什么样"，再看演示，然后在自己的魔方上操作。卡住了就回来单步对照。</p>
        `,
      },
      {
        id: 'step1-cross',
        title: '第 1 步：白色十字',
        goal: {
          caption:
            '底面四个白色棱块归位形成十字（角块尚未处理，乱着很正常）。注意"归位"有两个要求：白色朝下 + 侧面颜色和相邻中心一致',
          highlight: 'D_EDGES',
        },
        body: `
          <p><b>🎯 这一步做什么</b>：在底面（白色面）做出一个十字——四个白色棱块回到正确位置，白色朝下。</p>
          <p><b>先搞懂"归位"</b>：白色棱块有两个颜色，白色 + 另一个颜色。<b>另一个颜色就是它家的地址</b>：白-绿棱的家在绿色中心的正下方，白-红棱的家在红色中心正下方。只把白色转到底面还不够，侧面颜色也要对上。</p>
          <p><b>核心套路（对准 → 落下）</b>，就像电梯：先把电梯开到正确的楼层门口，再下去：</p>
          <ol>
            <li>找一个带白色的棱块，把它弄到<b>顶层</b>（如果它已经在顶层就跳过）</li>
            <li>转动顶层（<code>U</code>），让它的<b>侧面颜色对准同色中心</b>（绿对绿、红对红）</li>
            <li>把那个面转 180°（如 <code>F2</code>），棱块就垂直落到底面正确位置</li>
          </ol>
          <p><b>三种常见情况</b>（下方演示各有一个案例）：</p>
          <ul>
            <li><b>情况 A</b>：白棱在顶层、白色朝上 → 标准流程：对准 → 转 180° 落下</li>
            <li><b>情况 B</b>：白棱在顶层、但白色朝侧面（翻了面）→ 用 4 步"侧塞"进去（见案例演示）</li>
            <li><b>情况 C</b>：四个白棱依次归位的完整过程 → 看整体节奏</li>
          </ul>
          <p class="muted">⚠️ 常见错误：只看白色不看侧色，做完发现"十字是白的，但侧面颜色错位"。记住口诀：<b>先对色，再落下</b>。</p>
        `,
        demo: {
          view: 'bottom',
          highlight: 'D_EDGES',
          cases: [
            {
              id: 'drop',
              label: 'A · 对准再落（基本功）',
              brief: '白-绿棱在顶层、白色朝上：先转顶层对准绿色中心，再 F2 落位',
              setup: 'F2 U',
              formula: "U' F2",
              notes: [
                "U' —— 转顶层：把白-绿棱送到绿色中心的正上方（看它侧面的绿色，和绿中心对齐了）",
                'F2 —— 前面转 180°：白棱垂直落到底面。白色朝下 ✓ 绿色对绿中心 ✓ 归位！',
              ],
            },
            {
              id: 'flip',
              label: 'B · 白色朝侧面（翻面）',
              brief: '白-绿棱在顶层但白色朝前（翻面了）：别硬落，用 4 步侧塞进去',
              setup: "R' F' R U",
              formula: "U' R' F R",
              notes: [
                "U' —— 翻面的棱不能直接落（落下去白色会朝侧面）。先把它转到右前方",
                "R' —— 右层向下带：让这个棱块降到中层（借用了右边白-红棱的位置，稍后会还）",
                'F —— 前面顺时针：把它从中层"侧塞"进底面——这样白色刚好朝下！',
                'R —— 右层转回去：把刚才借用的白-红棱还回原位。四个臂完好 ✓',
              ],
            },
            {
              id: 'all4',
              label: 'C · 四棱依次归位全过程',
              brief: '4 个白棱都在顶层：对准一个落一个，看底面十字 0/4 → 4/4 长出来',
              setup: 'F2 R2 B2 L2 U2',
              formula: 'U2 F2 R2 B2 L2',
              notes: [
                'U2 —— 转顶层对准：让每个白棱的侧色对上同色中心（白绿对绿、白红对红……这里恰好一次全对准，实际通常要一个一个对）',
                'F2 —— 白-绿棱落位。底面十字 1/4 ✓（转到底面看看！）',
                'R2 —— 白-红棱落位。十字 2/4 ✓',
                'B2 —— 白-蓝棱落位。十字 3/4 ✓',
                'L2 —— 白-橙棱落位。十字完成 4/4 ✓✓ 每个臂的侧色都和中心一致',
              ],
            },
          ],
        },
      },
      {
        id: 'step2-corners',
        title: '第 2 步：白色角块',
        goal: {
          caption:
            '四个白色角块进到底面四个角，整个白色面完成，而且每个角的侧面颜色都和相邻中心对齐（第一层完成）',
          highlight: 'D_CORNERS',
        },
        body: `
          <p><b>🎯 这一步做什么</b>：白十字做好了，四个角还空着。这一步把 4 个白色角块一个个塞进去，完成整个第一层。</p>
          <p><b>只用一套动作</b>——就是记法课练过的四步连招（口诀：<b>掏、让、放、回</b>）：</p>
          <p class="formula"><code>R U R' U'</code></p>
          <p>整个第 2 步 = 对每个角块重复下面三件事：</p>
          <ol>
            <li><b>找家</b>：拿起一个带白色的角块，看它<b>另外两个颜色</b>。比如白-绿-红角块，它的家就在<b>绿色中心和红色中心夹着的那个底角</b>（本教程演示固定用"前右下"这个角）。</li>
            <li><b>送到家门口正上方</b>：转动顶层（<code>U</code>），把这个角块移到它家的<b>正上方</b>。</li>
            <li><b>反复做 <code>R U R' U'</code></b>：做完一遍看一眼——白色朝下了就停，没朝下就再做一遍。<b>最多 5 遍必定归位</b>。</li>
          </ol>
          <p><b>次数速查</b>（角块已在家门口正上方时，看白色贴纸朝哪）：</p>
          <table class="lesson-table">
            <tr><th>白色贴纸朝向</th><th>需要做几遍</th></tr>
            <tr><td>朝<b>右</b>（对着 R 面）</td><td>1 遍</td></tr>
            <tr><td>朝<b>上</b>（对着天）</td><td>3 遍</td></tr>
            <tr><td>朝<b>前</b>（对着你）</td><td>5 遍</td></tr>
          </table>
          <p class="muted">不用背这张表——<b>"没朝下就继续做"</b>就够了。表只是让你心里有数：这不是死循环，是在一步步接近。</p>
          <p><b>特殊情况：角块卡在底层</b>（在别人家里、或在自己家但装反了）？<b>不需要新公式</b>：把那个角所在的位置摆到前右下，照样做 <code>R U R' U'</code>——第 1 遍就会把它顶回顶层，然后按常规流程处理（下方案例 ⑤ 演示了全过程）。</p>
          <p class="muted">⚠️ 安心提示：连招做到一半时，魔方看起来"更乱了"（白十字的臂会暂时被带走）——<b>完全正常</b>，每遍做完 4 步它自己会回来。只认一个标准：<b>白色朝下才算完</b>。</p>
          <p class="muted">📐 下方演示默认<b>仰视视角</b>（能看到白色底面）。随时可以拖动魔方换角度，播放中也可以。</p>
        `,
        demo: {
          view: 'bottom',
          highlight: 'D_CORNERS',
          cases: [
            {
              id: 'c1',
              label: '① 白色朝右 → 1 遍',
              brief: '角块在家门口正上方、白色贴纸朝右：一遍连招直接归位',
              setup: "U R U' R'",
              formula: "R U R' U'",
              notes: sexyNotes(1, ['看底面：白色朝下，侧色对齐，归位 ✓']),
            },
            {
              id: 'c3',
              label: '② 白色朝上 → 3 遍',
              brief: '角块在家门口正上方、白色朝天：连做 3 遍，中途时好时坏别慌',
              setup: rep("U R U' R'", 3),
              formula: rep("R U R' U'", 3),
              notes: sexyNotes(3, [
                '角块进槽了，但白色朝右歪着——还没完，继续！',
                '角块又弹回顶层，白色变成朝右了——这就是"1 遍就好"的标准情况',
                '白色朝下，归位 ✓（朝上的角块就是固定 3 遍）',
              ]),
            },
            {
              id: 'c5',
              label: '③ 白色朝前 → 5 遍',
              brief: '角块在家门口正上方、白色朝你：连做 5 遍。记不住没关系，没朝下就继续',
              setup: rep("U R U' R'", 5),
              formula: rep("R U R' U'", 5),
              notes: sexyNotes(5, [
                '进槽了，白色朝前歪着——继续',
                '弹回顶层，白色朝上了',
                '又进槽，白色朝右歪着——继续',
                '弹回顶层，白色朝右——标准情况，最后一遍',
                '白色朝下，归位 ✓（每遍结束瞄一眼白色朝向，你就永远不会迷路）',
              ]),
            },
            {
              id: 'align',
              label: '④ 不在正上方？先对位',
              brief: '角块在顶层别处（左前上）：先转 U 把它送到家门口正上方，再照常连招',
              setup: "U R U' R' U",
              formula: "U' R U R' U'",
              notes: [
                "U' —— 找家！这个白-绿-红角块的家在前右下（绿、红中心夹角）。转顶层把它送到家门口正上方。到位后白色朝右——查表：1 遍",
                ...sexyNotes(1, ['白色朝下，归位 ✓（先对位、再连招，永远这个顺序）']),
              ],
            },
            {
              id: 'stuck',
              label: '⑤ 卡在底层装反了',
              brief: '角块已在自己家但白色朝前（装反）：照样连招，第 1 遍自动把它顶出来',
              setup: rep("U R U' R'", 4),
              formula: rep("R U R' U'", 4),
              notes: sexyNotes(4, [
                '看！装反的角块被顶回顶层了（白色朝上）——问题回到了熟悉的情况',
                '进槽，白色朝右歪着——继续',
                '弹出，白色朝右——标准情况，最后一遍',
                '白色朝下，这次装正了 ✓（卡在别人家里同理：先顶出来，再对位处理）',
              ]),
            },
          ],
        },
      },
      {
        id: 'step3-middle',
        title: '第 3 步：中层棱块',
        goal: {
          caption: '四个中层棱块归位，前两层（下两层）全部完成',
          highlight: 'MIDDLE_EDGES',
        },
        body: `
          <p><b>🎯 这一步做什么</b>：把四个中层棱块归位，让下面两层全部完成。</p>
          <p><b>哪些棱块属于中层</b>：<b>既不带白色、也不带黄色</b>的棱块，共 4 个（绿红、绿橙、蓝红、蓝橙）。它们的家在中间那一层的四个侧位。</p>
          <p><b>操作套路</b>：</p>
          <ol>
            <li>在顶层找一个<b>不带黄色</b>的棱块（带黄的都是顶层自己的，别管）</li>
            <li>转 <code>U</code>，让它<b>侧面的颜色对准同色中心</b>（比如绿色面对准绿色中心）——现在它就悬在自己家的上方</li>
            <li>看它<b>顶面的颜色</b>该去左边还是右边：顶面颜色和右边中心一样 → 往右插；和左边中心一样 → 往左插</li>
          </ol>
          <p><b>往右插入</b>：</p>
          <p class="formula"><code>U R U' R' &nbsp;U' F' U F</code></p>
          <p><b>往左插入</b>（右插的镜像）：</p>
          <p class="formula"><code>U' L' U L &nbsp;U F U' F'</code></p>
          <p class="muted">💡 公式看着长，其实是两半：<b>前 4 步把棱块"借"进槽口，后 4 步把它"塞"到位并把借走的东西还回去</b>。跟着单步演示走一遍就有感觉了。中途白色面会被暂时带乱，做完自动恢复。</p>
          <p class="muted">⚠️ 顶层找不到不带黄的棱块，但中层有棱块插错了位置/方向？用案例 ③ 的办法：拿任意一个顶层棱块对它做一次插入公式，把它"顶"出来，再按常规插回去。</p>
        `,
        demo: {
          view: 'front',
          highlight: 'MIDDLE_EDGES',
          cases: [
            {
              id: 'right',
              label: '① 往右插入',
              brief: '绿-红棱在顶层、绿色已对准绿中心、顶面红色 → 它该去右边',
              setup: "F' U' F U R U R' U'",
              formula: "U R U' R' U' F' U F",
              notes: [
                'U —— 【前半·借位】先把棱块转离槽口，右边让路',
                'R —— 右槽打开（右层上提）',
                "U' —— 棱块送到槽口",
                "R' —— 右槽合上：棱块暂时「寄存」在顶层旁边",
                "U' —— 【后半·入位】换个方向，准备从前面接它",
                "F' —— 前槽打开",
                'U —— 把棱块推进去',
                'F —— 前槽合上：绿-红棱进入中层右位，前两层完好 ✓',
              ],
            },
            {
              id: 'left',
              label: '② 往左插入',
              brief: '绿-橙棱在顶层、绿色已对准绿中心、顶面橙色 → 它该去左边（右插的镜像）',
              setup: "F U F' U' L' U' L U",
              formula: "U' L' U L U F U' F'",
              notes: [
                "U' —— 【前半·借位】棱块转离槽口，左边让路",
                "L' —— 左槽打开",
                'U —— 棱块送到槽口',
                'L —— 左槽合上：棱块寄存在顶层旁边',
                'U —— 【后半·入位】换个方向接它',
                'F —— 前槽打开（注意和右插方向相反）',
                "U' —— 棱块推进去",
                "F' —— 前槽合上：绿-橙棱进入中层左位 ✓",
              ],
            },
            {
              id: 'requeue',
              label: '③ 插反了→顶出重插',
              brief: '绿-红棱已在右槽里但方向反了：先用公式把它顶出来，再正常插回',
              setup: "F' U' F U R U R' U' U2 F' U' F U R U R' U'",
              formula: "U R U' R' U' F' U F U2 U R U' R' U' F' U F",
              notes: [
                'U —— 【顶出阶段】对着装反的槽做一次右插公式：随便塞一个棱进去……',
                'R —— （右槽打开）',
                "U' —— （替换棱送入）",
                "R' —— （右槽合上）",
                "U' —— （换方向）",
                "F' —— （前槽打开）",
                'U —— （推入）',
                'F —— 替换棱占了槽位，装反的绿-红棱被顶回了顶层！',
                'U2 —— 【对位】把顶出来的绿-红棱转到绿色中心上方对齐',
                'U —— 【重插阶段】现在按标准右插公式来一遍',
                'R —— 右槽打开',
                "U' —— 棱块送到槽口",
                "R' —— 右槽合上",
                "U' —— 换方向",
                "F' —— 前槽打开",
                'U —— 推入',
                'F —— 这次方向对了，归位 ✓',
              ],
            },
          ],
        },
      },
      {
        id: 'step4-top-cross',
        title: '第 4 步：黄色十字',
        goal: {
          caption: '顶面四个棱块的黄色面朝上，形成黄色十字（这一步不管侧面颜色）',
          highlight: 'U_EDGES',
        },
        body: `
          <p><b>🎯 这一步做什么</b>：在黄色面（顶面）做出十字——四个黄色棱块的黄色朝上。<b>只看顶面，不管侧面颜色</b>（侧面留给第 7 步）。</p>
          <p><b>只有一个公式</b>：</p>
          <p class="formula"><code>F R U R' U' F'</code></p>
          <p>它 = F 开头 + 熟悉的四步连招 + F' 收尾。作用：把顶层某些棱块的朝向翻一下。</p>
          <p><b>看顶面黄色棱块的形状</b>，决定怎么摆、做几次：</p>
          <ul>
            <li><b>点</b>（没有黄棱朝上）：随便什么方向，做 1 次 → 变 L 形</li>
            <li><b>L 形</b>（两个相邻）：把 L 摆到<b>左上角</b>（9 点钟方向），做 1 次 → 变一字</li>
            <li><b>一字</b>（两个相对）：把一字摆<b>横向</b>，做 1 次 → 十字完成</li>
            <li><b>十字</b>：跳过，进入第 5 步</li>
          </ul>
          <p class="muted">💡 所以最坏情况（点）就是连做 3 次，每次做之前按上面规则摆一下方向。记忆：<b>点 → L → 一字 → 十字</b>，一条流水线。</p>
        `,
        demo: {
          view: 'top',
          highlight: 'U_EDGES',
          cases: [
            {
              id: 'line',
              label: '① 一字 → 十字',
              brief: '顶面是横向"一字"（两个黄棱相对）：做 1 次公式变十字',
              setup: "F U R U' R' F'",
              formula: "F R U R' U' F'",
              notes: [
                'F —— 开门：把前面转起来（给连招搭个台）',
                'R —— 连招①掏',
                'U —— 连招②让',
                "R' —— 连招③放",
                "U' —— 连招④回",
                "F' —— 关门：前面转回去。看顶面——黄色十字完成 ✓",
              ],
            },
            {
              id: 'L',
              label: '② L 形 → 一字',
              brief: 'L 形摆在左上角（9 点钟）：做 1 次公式变一字，然后按案例①继续',
              setup: "F U R U' R' F' U2 F U R U' R' F'",
              formula: "F R U R' U' F'",
              notes: [
                'F —— 开门（确认 L 的两个黄棱指向左上角再开始）',
                'R —— 连招①掏',
                'U —— 连招②让',
                "R' —— 连招③放",
                "U' —— 连招④回",
                "F' —— 关门。L 变成了一字！把一字摆横向，再做一次就是十字（见案例①）",
              ],
            },
          ],
        },
      },
      {
        id: 'step5-top-color',
        title: '第 5 步：黄色面全黄',
        goal: {
          caption: '顶面 9 格全部变黄（四个角块的黄色也朝上）',
          highlight: 'U_ALL',
        },
        body: `
          <p><b>🎯 这一步做什么</b>：黄十字有了，再让四个<b>角块</b>的黄色也朝上——顶面 9 格全黄。</p>
          <p><b>核心公式（Sune，"死内"）</b>：</p>
          <p class="formula"><code>R U R' U &nbsp;R U2 R'</code></p>
          <p><b>怎么用</b>：</p>
          <ol>
            <li>数一数顶面<b>已经朝上的黄色角</b>有几个，把魔方整体转到对应姿势（<b>转整个魔方，不转 U</b>）：
              <ul>
                <li><b>1 个朝上</b>：把那个角放<b>左前</b></li>
                <li><b>0 个朝上</b>：让<b>左前角的黄色贴纸朝左</b></li>
                <li><b>2 个朝上</b>：让<b>左前角的黄色贴纸朝前</b></li>
              </ul>
            </li>
            <li>做一次 Sune</li>
            <li>还没全黄？回到第 1 步再来（最多 2~3 次）</li>
          </ol>
          <p class="muted">⚠️ 这一步会暂时打乱前两层，做完自动恢复——只要你转的是整个魔方和 U/R 层，不要中途转别的面。</p>
        `,
        demo: {
          view: 'top',
          highlight: 'U_CORNERS',
          cases: [
            {
              id: 'sune',
              label: 'Sune 一次全黄',
              brief: '一个黄角已朝上（放在左前），做一次 Sune → 顶面全黄',
              setup: "R U2 R' U' R U' R'",
              formula: "R U R' U R U2 R'",
              notes: [
                'R —— 右层上提',
                'U —— 顶层转（带走一个角）',
                "R' —— 右层放回",
                'U —— 顶层再转',
                'R —— 右层再提',
                'U2 —— 顶层转 180°',
                "R' —— 右层放回。看顶面：全黄 ✓（没全黄就按规则摆好姿势再来一次）",
              ],
            },
          ],
        },
      },
      {
        id: 'step6-top-corners',
        title: '第 6 步：顶层角块位置',
        goal: {
          caption: '四个角块换到正确位置：每个角的侧面两色与相邻的两个中心一致',
          highlight: 'U_CORNERS',
        },
        body: `
          <p><b>🎯 这一步做什么</b>：顶面全黄了，但角块可能站错了位置（该在左前的跑到了右前）。这一步把四个角换到正确位置——而且<b>不能破坏刚做好的全黄面</b>，所以要用一个"只搬位置、不拧朝向"的公式。</p>
          <p><b>怎么判断一个角"位置对"</b>：看它侧面的两个颜色，是否等于它所在角落的两个中心颜色（顺序无所谓）。比如右前角的侧面应该是"红+绿"。</p>
          <p><b>核心公式</b>（三个角轮换，<b>左前角不动</b>，做完顶面依旧全黄）：</p>
          <p class="formula"><code>R' F R' B2 &nbsp;R F' R' B2 &nbsp;R2</code></p>
          <p class="muted">节奏很规整：右-前-右-后²｜右-前-右-后²｜右²。前半段把角块「装车」，B2 负责搬运，最后 R2 收尾。</p>
          <p><b>怎么用（三条规则，跟着走一定能完成，全程最多做 3 次公式）</b>：</p>
          <ol>
            <li><b>先转动顶层（<code>U</code>）找归位角</b>：一格一格转，每转一格检查一遍"哪些角的侧面两色和两边中心对上了"。<b>一定至少能让一个角归位</b>。</li>
            <li><b>数一数归位了几个角</b>：
              <ul>
                <li><b>4 个</b> → 这一步完成，直接去第 7 步 🎉</li>
                <li><b>恰好 1 个</b> → 把它握在<b>左前</b>（转整个魔方来摆，别再转 U），做一次公式；没全对就再做一次（这种情况最多 2 次必好）</li>
                <li><b>2 个归位、另外 2 个互换</b> → 这是特殊情况：三轮换公式没法直接换 2 个。<b>先随便做一次公式</b>（把互换打散），然后<b>回到第 1 条重新转 U 找</b>——这时一定会变成"恰好 1 个"的熟悉情况</li>
              </ul>
            </li>
            <li>做完随时用规则 1 复查，四个角全对就收工</li>
          </ol>
          <p class="muted">⚠️ 公式做到一半顶面会暂时乱掉——正常，最后一步做完自动恢复全黄。</p>
          <p class="muted">🆘 如果发现顶面黄色不全了（多半是中途做错了一步）：别慌，退回<b>第 5 步</b>用 Sune 把顶面重新做全黄，再回来走这一步的流程。</p>
        `,
        demo: {
          view: 'top',
          highlight: 'U_CORNERS',
          cases: [
            {
              id: 'cycle',
              label: '① 恰好 1 个归位',
              brief: '标准情况：左前角已归位，另外三个角轮换错位 → 做 1 次公式全部到位',
              setup: "R2 B2 R F R' B2 R F' R",
              formula: "R' F R' B2 R F' R' B2 R2",
              notes: [
                "R' —— 右层下拉，开始「搬运」（中途顶面暂时乱掉是正常的）",
                'F —— 前面顺转：把第一个角块「装车」',
                "R' —— 右层再下拉",
                'B2 —— 后面 180°：把角块运到后场',
                'R —— 右层放回',
                "F' —— 前面转回",
                "R' —— 右层又下拉：接第二个角块",
                'B2 —— 后面 180°：第二趟搬运',
                'R2 —— 右层 180° 收尾：三个角各就各位，顶面恢复全黄 ✓ 检查每个角的侧面两色是否对上中心',
              ],
            },
            {
              id: 'swap',
              label: '② 2 个归位 2 个互换',
              brief: '特殊情况：右前和左后已归位，剩下两个角对角互换 → 做一遍打散、转 U 对位、同样拿法再做一遍',
              setup: "R2 B2 R F R' B2 R F' R U' R2 B2 R F R' B2 R F' R",
              formula: "R' F R' B2 R F' R' B2 R2 U R' F R' B2 R F' R' B2 R2",
              notes: [
                "R' —— 【第一遍：打散互换】三轮换公式没法直接对付「两两互换」，先随便做一遍",
                'F —— （装车）',
                "R' —— ",
                'B2 —— （搬运）',
                'R —— ',
                "F' —— ",
                "R' —— ",
                'B2 —— （第二趟）',
                'R2 —— 第一遍完成：互换被打散了，现在一个归位的都没有——别慌，按规则去转 U 重新找',
                'U —— 【转顶层对位】转一格检查：左前角归位了！回到了熟悉的"恰好 1 个"情况',
                "R' —— 【第二遍：标准三轮换】拿法不变，公式一模一样",
                'F —— （装车）',
                "R' —— ",
                'B2 —— （搬运）',
                'R —— ',
                "F' —— ",
                "R' —— ",
                'B2 —— （第二趟）',
                'R2 —— 收尾：四个角全部归位，顶面依旧全黄 ✓✓',
              ],
            },
          ],
        },
      },
      {
        id: 'step7-top-edges',
        title: '第 7 步：顶层棱块归位',
        goal: {
          caption: '四个棱块归位——魔方六个面全部还原！',
          highlight: 'U_EDGES',
        },
        body: `
          <p><b>🎯 这一步做什么</b>：最后一步！把顶层四个棱块换到正确位置，魔方复原。</p>
          <p><b>核心公式</b>（三个棱轮换，后面那条棱不动）：</p>
          <p class="formula"><code>R U' R U R U &nbsp;R U' R' U' R2</code></p>
          <p><b>怎么用</b>：</p>
          <ol>
            <li>找一条<b>已经归位</b>的棱（侧色和中心一致），把它转到<b>后面</b>（背对你）</li>
            <li>做一次公式 → 其余三条棱轮换</li>
            <li>没全对？再做一次</li>
          </ol>
          <p class="muted">💡 一条归位的都没有？随便做一次，就会出现一条。做完这一步——🎉 恭喜，魔方还原了！</p>
        `,
        demo: {
          view: 'top',
          highlight: 'U_EDGES',
          cases: [
            {
              id: 'cycle',
              label: '三棱轮换收官',
              brief: '后面那条棱已归位：一次公式让前/左/右三条棱轮换到位 → 完成还原',
              setup: "R2 U R U R' U' R' U' R' U R'",
              formula: "R U' R U R U R U' R' U' R2",
              notes: [
                'R —— 起手',
                "U' —— 顶层拨一格",
                'R —— ',
                'U —— ',
                'R —— ',
                'U —— ',
                'R —— 这一段是"泵"的节奏：R 和 U 交替，把三条棱推着走',
                "U' —— ",
                "R' —— ",
                "U' —— ",
                'R2 —— 收尾转 180°。六面全部还原 🎉🎉 恭喜完成层先法！',
              ],
            },
          ],
        },
      },
    ],
  },
  CFOP_SECTION,
];
